import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { type UserRow } from './tables/userManagementTypes';

export interface InviteReminderMessage {
    title: string;
    message: string;
}

interface InvitationReminderDialogProps {
    open: boolean;
    invitations: UserRow[];
    onOpenChange: (open: boolean) => void;
    onSend: (message: InviteReminderMessage) => Promise<boolean | void>;
}

export default function InvitationReminderDialog({
    open,
    invitations,
    onOpenChange,
    onSend,
}: InvitationReminderDialogProps) {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setTitle(t('facilities.reminderDialog.defaultTitle'));
            setMessage(t('facilities.reminderDialog.defaultMessage'));
            setError('');
        }
    }, [open, t]);

    const handleSend = async () => {
        if (!title.trim() || !message.trim()) {
            setError(t('facilities.reminderDialog.validationError'));
            return;
        }

        setIsSending(true);
        try {
            const result = await onSend({
                title: title.trim(),
                message: message.trim(),
            });
            if (result === false) {
                setError(t('facilities.reminderDialog.sendError'));
            } else {
                onOpenChange(false);
            }
        } catch {
            setError(t('facilities.reminderDialog.sendError'));
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('facilities.reminderDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {invitations.length === 1
                            ? t('facilities.reminderDialog.descSingle', { email: invitations[0].email })
                            : t('facilities.reminderDialog.descMultiple', { count: invitations.length })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="reminder-title">{t('facilities.reminderDialog.titleLabel')}</Label>
                        <Input
                            id="reminder-title"
                            value={title}
                            onChange={(event) => {
                                setTitle(event.target.value);
                                setError('');
                            }}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="reminder-message">{t('facilities.reminderDialog.messageLabel')}</Label>
                        <textarea
                            id="reminder-message"
                            className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={message}
                            onChange={(event) => {
                                setMessage(event.target.value);
                                setError('');
                            }}
                        />
                    </div>
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSending}
                    >
                        {t('facilities.reminderDialog.cancel')}
                    </Button>
                    <Button onClick={handleSend} disabled={isSending}>
                        {isSending ? t('facilities.reminderDialog.sending') : t('facilities.reminderDialog.send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
